import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { cn } from "../../lib/cn";

/*
 * A vertical list that can be reordered smoothly with a pointer (mouse, pen, touch)
 * or the keyboard (Alt + ↑/↓ on a focused handle). Items glide out of the way while
 * dragging and every order change is animated (FLIP), so moves never "jump".
 */

const EASE = "transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)";

export interface HandleProps {
  onPointerDown: (e: PointerEvent<HTMLElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  "aria-roledescription": string;
  "aria-describedby"?: string;
  style: CSSProperties;
}

interface Drag {
  id: string;
  pointerId: number;
  startY: number;
  dy: number;
  from: number;
  over: number;
  /** Item tops and heights captured when the drag started. */
  tops: number[];
  heights: number[];
  min: number;
  max: number;
  /** Past the small threshold that separates a click from a drag. */
  moved: boolean;
}

function scrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let n = el?.parentElement; n; n = n.parentElement) {
    const o = getComputedStyle(n).overflowY;
    if ((o === "auto" || o === "scroll") && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
}

export function SortableList<T>({
  items,
  getId,
  canMove,
  onMove,
  renderItem,
  className,
  label,
  gap = 2,
}: {
  items: T[];
  getId: (item: T) => string;
  canMove: (item: T) => boolean;
  /** Called with the item id and its new index once a move is committed. */
  onMove: (id: string, toIndex: number) => void;
  renderItem: (item: T, state: { dragging: boolean; handle: HandleProps; movable: boolean }) => ReactNode;
  className?: string;
  label: string;
  /** Vertical gap between items in px (must match the list's spacing). */
  gap?: number;
}) {
  const refs = useRef(new Map<string, HTMLLIElement>());
  const snapshot = useRef<Map<string, number> | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [announce, setAnnounce] = useState("");
  const dragRef = useRef<Drag | null>(null);
  dragRef.current = drag;
  const ids = items.map(getId);

  /** Record where every item is on screen right now (including drag offsets). */
  const takeSnapshot = () => {
    const m = new Map<string, number>();
    refs.current.forEach((el, id) => m.set(id, el.getBoundingClientRect().top));
    snapshot.current = m;
  };

  // FLIP: after the order changes, animate each item from where it was to where it is.
  useLayoutEffect(() => {
    const before = snapshot.current;
    if (!before) return;
    snapshot.current = null;
    refs.current.forEach((el, id) => {
      const prev = before.get(id);
      if (prev === undefined) return;
      const delta = prev - el.getBoundingClientRect().top;
      if (!delta) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
      void el.offsetHeight; // commit the inverted position before animating back
      el.style.transition = EASE;
      el.style.transform = "";
      const done = () => {
        el.style.transition = "";
        el.removeEventListener("transitionend", done);
      };
      el.addEventListener("transitionend", done);
    });
  }, [ids.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const movableRange = () => {
    const idx = items.map((it, i) => (canMove(it) ? i : -1)).filter((i) => i >= 0);
    return { min: idx[0] ?? 0, max: idx[idx.length - 1] ?? items.length - 1 };
  };

  const start = (e: PointerEvent<HTMLElement>, id: string, index: number) => {
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const rects = ids.map((x) => refs.current.get(x)!.getBoundingClientRect());
    const { min, max } = movableRange();
    setDrag({ id, pointerId: e.pointerId, startY: e.clientY, dy: 0, from: index, over: index, tops: rects.map((r) => r.top), heights: rects.map((r) => r.height), min, max, moved: false });
  };

  const move = (e: PointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dy = e.clientY - d.startY;
    const center = d.tops[d.from] + dy + d.heights[d.from] / 2;
    let over = d.from;
    for (let i = 0; i < d.tops.length; i++) {
      const mid = d.tops[i] + d.heights[i] / 2;
      if (i < d.from && center < mid) {
        over = i;
        break;
      }
      if (i > d.from && center > mid) over = i;
    }
    over = Math.min(d.max, Math.max(d.min, over));
    // Nudge the scroll container when dragging near its edges.
    const sp = scrollParent(refs.current.get(d.id) ?? null);
    if (sp) {
      const r = sp.getBoundingClientRect();
      if (e.clientY < r.top + 40) sp.scrollTop -= 10;
      else if (e.clientY > r.bottom - 40) sp.scrollTop += 10;
    }
    setDrag({ ...d, dy, over, moved: d.moved || Math.abs(dy) > 3 });
  };

  const end = (e: PointerEvent<HTMLElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    takeSnapshot();
    setDrag(null);
    if (d.over !== d.from) {
      onMove(d.id, d.over);
      setAnnounce(`Moved to position ${d.over + 1} of ${items.length}`);
    }
  };

  const keyMove = (e: KeyboardEvent<HTMLElement>, id: string, index: number) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    const { min, max } = movableRange();
    const to = e.key === "ArrowUp" ? index - 1 : index + 1;
    if (to < min || to > max) return;
    takeSnapshot();
    onMove(id, to);
    setAnnounce(`Moved to position ${to + 1} of ${items.length}`);
    // Keep focus on the moved item's handle after re-render.
    requestAnimationFrame(() => refs.current.get(id)?.querySelector<HTMLElement>("[data-sort-handle]")?.focus());
  };

  /** Where a non-dragged item should sit visually during a drag. */
  const shiftFor = (i: number): number => {
    if (!drag || i === drag.from) return 0;
    const h = drag.heights[drag.from] + gap;
    if (drag.from < drag.over && i > drag.from && i <= drag.over) return -h;
    if (drag.from > drag.over && i < drag.from && i >= drag.over) return h;
    return 0;
  };

  return (
    <>
      <ul className={cn("relative", className)} aria-label={label} onPointerMove={move} onPointerUp={end} onPointerCancel={end}>
        {items.map((item, i) => {
          const id = getId(item);
          const dragging = drag?.id === id && drag.moved;
          const movable = canMove(item);
          const style: CSSProperties = dragging
            ? { transform: `translateY(${drag!.dy}px)`, zIndex: 10, position: "relative", transition: "none" }
            : drag?.moved
              ? { transform: `translateY(${shiftFor(i)}px)`, transition: EASE }
              : {};
          return (
            <li
              key={id}
              ref={(el) => {
                if (el) refs.current.set(id, el);
                else refs.current.delete(id);
              }}
              style={style}
              className={cn(dragging && "rounded-md shadow-pop")}
            >
              {renderItem(item, {
                dragging,
                movable,
                handle: {
                  onPointerDown: (e) => movable && start(e, id, i),
                  onKeyDown: (e) => movable && keyMove(e, id, i),
                  "aria-roledescription": "sortable item",
                  style: { touchAction: "none", cursor: movable ? (dragging ? "grabbing" : "grab") : undefined },
                },
              })}
            </li>
          );
        })}
      </ul>
      <span role="status" aria-live="polite" className="sr-only">
        {announce}
      </span>
    </>
  );
}
